import type { Session } from "./types";

// Токен держим в sessionStorage, а не в localStorage: служебная сессия
// не должна переживать закрытие вкладки. Срок жизни и таймаут бездействия
// проверяет сервер — здесь только удобство.
const TOKEN_KEY = "patrol.token";

export function storedToken(): string | null {
  try {
    return sessionStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function storeToken(token: string | null) {
  try {
    if (token === null) sessionStorage.removeItem(TOKEN_KEY);
    else sessionStorage.setItem(TOKEN_KEY, token);
  } catch {
    // Приватный режим браузера может запрещать хранилище — работаем без него.
  }
}

export class ApiError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
  }
}

/** Вызывается, когда сервер перестал принимать токен: сессия истекла. */
let onUnauthorized: () => void = () => {};
export function setUnauthorizedHandler(fn: () => void) {
  onUnauthorized = fn;
}

async function send(path: string, init?: RequestInit): Promise<Response> {
  const token = storedToken();
  const res = await fetch(path, {
    ...init,
    headers: {
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init?.headers,
    },
  });

  if (res.status === 401) {
    storeToken(null);
    onUnauthorized();
    throw new ApiError(401, "Сессия завершена, войдите заново");
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, body.error ?? `Ошибка ${res.status}`);
  }
  return res;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await send(path, init);
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  // Файлы тоже требуют токена, поэтому <img src> или ссылка напрямую не работают.
  blob: (path: string) => send(path).then((res) => res.blob()),

  /** Скачивание файла с авторизацией: имя берётся из ответа сервера.
   *  body — для выдач, которые сервер требует подтвердить (POST). */
  async download(path: string, fallbackName: string, body?: unknown): Promise<void> {
    const res = await send(path, body === undefined ? undefined : { method: "POST", body: JSON.stringify(body) });
    const name = /filename="([^"]+)"/.exec(res.headers.get("Content-Disposition") ?? "")?.[1] ?? fallbackName;
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PUT", body: JSON.stringify(body) }),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "POST", body: body ? JSON.stringify(body) : undefined }),

  async login(login: string, password: string): Promise<Session> {
    const session = await request<Session>("/api/v1/auth/login", {
      method: "POST",
      body: JSON.stringify({ login, password }),
      headers: { "Content-Type": "application/json" },
    });
    storeToken(session.token);
    return session;
  },

  async logout(): Promise<void> {
    try {
      await request<void>("/api/v1/auth/logout", { method: "POST" });
    } finally {
      storeToken(null);
    }
  },
};

/** Сборка строки запроса без пустых параметров. */
export function query(params: Record<string, string | number | boolean | undefined | null>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== "") q.set(k, String(v));
  }
  const s = q.toString();
  return s ? `?${s}` : "";
}
